import { UcoastEl } from '@/scripts/core/UcoastEl'
import { qsRequired } from '@/scripts/core/global'
import { uCoastWindow } from '@/scripts/setup'
import { type Hls } from '@/scripts/global'

declare let window: uCoastWindow

function sleep(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

export class UcoastVideo extends UcoastEl {
	static htmlSelector = 'ucoast-video'
	videoEl: HTMLVideoElement
	hasHls: boolean
	hlsReady: boolean
	hlsSource?: string
	mp4Source?: string
	initialized = false
	preloaded: boolean
	isLoading: boolean
	isPlaying: boolean
	hasPlayed: boolean
	hlsInstance: Hls
	isTestMode: boolean
	preloadPauseTimer?: number
	pauseAfterPreload = false

	constructor() {
		super()
		const { videoEl, hasHls, hlsReady, hlsSource, mp4Source } = this.init()
		this.videoEl = videoEl
		this.hasHls = hasHls
		this.hlsReady = hlsReady
		this.hlsSource = hlsSource
		this.mp4Source = mp4Source
		this.preloaded = false
		this.isLoading = false
		this.hasPlayed = false
		this.isPlaying = false
		this.isTestMode = this.hasAttribute('data-test-mode')
	}

	init() {
		const videoEl = qsRequired<HTMLVideoElement>('video', this)
		const hlsSource = videoEl.getAttribute('data-hls-src')?.trim() || undefined
		const hasHls = hlsSource !== undefined
		this.initialized = true
		return {
			videoEl,
			hasHls,
			hlsReady: false,
			hlsSource,
			mp4Source: videoEl.getAttribute('data-mp4-src')?.trim() || undefined,
		}
	}

	override async connectedCallback() {
		super.connectedCallback()
	}

	async play() {
		if (this.isHiddenForViewport()) return
		this.pauseAfterPreload = false
		window.clearTimeout(this.preloadPauseTimer)
		// if (this.isPlaying || this.isLoading) return
		if (this.isPlaying) return

		this.isLoading = true
		console.log('window.Ucoast.mediaManager.hlsRequired', window.Ucoast.mediaManager.hlsRequired)
		console.log('window.Ucoast.mediaManager.hlsLibraryLoaded', window.Ucoast.mediaManager.hlsLibraryLoaded)
		if (
			window.Ucoast.mediaManager.hlsRequired &&
			window.Ucoast.mediaManager.hlsLibraryLoaded &&
			!this.hasPlayed
		) {
			if (this.isTestMode) {
				console.log('[TEST MODE] 72')
			}
			await this.playHLS()
		} else {
			if (this.isTestMode) {
				console.log('[TEST MODE] 77')
			}
			await this.playStandard()
		}
	}

	async preload() {
		if (
			this.isPlaying ||
			this.preloaded ||
			this.hasPlayed ||
			this.isLoading
		)
			return

		if (this.isHiddenForViewport()) return

		this.pauseAfterPreload = true
		this.isLoading = true
		if (
			window.Ucoast.mediaManager.hlsRequired &&
			window.Ucoast.mediaManager.hlsLibraryLoaded &&
			!this.hasPlayed
		) {
			await this.preloadHLS()
			this.isLoading = false
		} else {
			await this.preloadStandard()
			this.isLoading = false
		}
	}

	async playStandard() {
		try {
			if (!this.hlsInstance) {
				const nativeHls =
					this.videoEl.canPlayType('application/vnd.apple.mpegurl') ||
					this.videoEl.canPlayType('audio/mpegurl')
				const source = nativeHls && this.hlsSource ? this.hlsSource : this.mp4Source
				if (!source) return
				if (
					this.videoEl.currentSrc !== new URL(source, document.baseURI).href &&
					this.videoEl.getAttribute('src') !== source
				)
					this.videoEl.src = source
			}
			this.videoEl.muted = true
			this.videoEl.currentTime = 0
			await sleep(1)
			await this.videoEl.play()
			this.hasPlayed = true
			this.preloaded = true
			this.isPlaying = !this.videoEl.paused
			this.setAttribute('data-uc-has-played', 'true')
		} catch {
			// Autoplay denial and interrupted loads must leave a retryable player and its poster.
			this.isPlaying = false
		} finally {
			this.isLoading = false
		}
	}

	async preloadStandard() {
		await this.playStandard()
		if (!this.isPlaying || !this.pauseAfterPreload) return
		this.preloadPauseTimer = window.setTimeout(() => {
			this.videoEl.pause()
			this.isPlaying = false
		}, 3)
	}

	async preloadHLS() {
		if (
			window.Ucoast.mediaManager.hlsLibIsSupported &&
			this.hlsSource &&
			!this.preloaded
		) {
			this.hlsInstance = new window.Ucoast.mediaManager.Hls.default()
			this.hlsInstance.on('hlsMediaAttached', async () => {
				await this.preloadStandard()
			})

			this.videoEl.src = this.hlsSource
			await this.hlsInstance.loadSource(this.hlsSource)
			await this.hlsInstance.attachMedia(this.videoEl)
		} else {
			await this.preloadStandard()
		}
	}

	async playHLS() {
		if (this.isTestMode) {
			console.log('[TEST MODE] 158')
		}
		if (
			window.Ucoast.mediaManager.hlsLibIsSupported &&
			this.hlsSource &&
			!this.preloaded
		) {
			this.hlsInstance = new window.Ucoast.mediaManager.Hls.default()
			this.hlsInstance.on('hlsMediaAttached', async () => {
				if (this.isTestMode) {
					console.log('[TEST MODE] 168')
				}
				await this.playStandard()
			})

			this.videoEl.src = this.hlsSource
			await this.hlsInstance.loadSource(this.hlsSource)
			await this.hlsInstance.attachMedia(this.videoEl)
		} else {
			if (this.isTestMode) {
				console.log('[TEST MODE] 178')
			}
			await this.playStandard()
		}
	}

	async pause() {
		if (this.isTestMode) {
			console.log('[TEST MODE] 186')
		}
		this.videoEl.pause()
		this.videoEl.currentTime = 0
		this.isPlaying = false
	}

	isHiddenForViewport() {
		return (
			this.videoEl.getBoundingClientRect().height < 1 ||
			this.videoEl.getBoundingClientRect().width < 1
		)
	}
}
